import type { AppDispatch } from '../Modules/Presentation/store';
import {
  fetchSlotsBatchDetail,
} from '../Modules/Slot/Presentation/actions';
import { finishSlotsRankCalculating } from '../Modules/Slot/Presentation/reducers';
import {
  RANK_POLL_INTERVAL_MS,
  RANK_POLL_MAX_MS,
  RANK_POLL_DELAYED_INTERVAL_MS,
  isRankCalculationComplete,
} from './slotRankAsync';

type PollSession = {
  timer: ReturnType<typeof setTimeout> | null;
  aborted: boolean;
};

const sessions = new Map<string, PollSession>();

function sessionKey(ids: number[]): string {
  return [...ids].filter((id) => id > 0).sort((a, b) => a - b).join(',');
}

/**
 * 생성/수정 직후 MID·순위 백그라운드 반영을 위해 batch_detail 폴링.
 * 처음 2분은 15초 간격, 그 뒤에는 30초 간격으로 결과가 기록될 때까지 재조회한다.
 * rank_history가 기록되면 숫자 순위·순위 밖 모두 완료다.
 */
export function scheduleSlotRankPolling(dispatch: AppDispatch, ids: number[]): void {
  const targetIds = Array.from(new Set(ids.filter((id) => id > 0)));
  if (targetIds.length === 0) return;

  const key = sessionKey(targetIds);
  const prev = sessions.get(key);
  if (prev) {
    prev.aborted = true;
    if (prev.timer) clearTimeout(prev.timer);
  }

  const session: PollSession = { timer: null, aborted: false };
  sessions.set(key, session);

  const startedAt = Date.now();
  let pendingIds = [...targetIds];

  const finish = (doneIds: number[]) => {
    if (doneIds.length) {
      dispatch(finishSlotsRankCalculating(doneIds));
    }
  };

  const tick = async () => {
    if (session.aborted || pendingIds.length === 0) return;

    try {
      const result = await dispatch(fetchSlotsBatchDetail(pendingIds)).unwrap();
      const stillPending: number[] = [];
      const resolved: number[] = [];

      for (const id of pendingIds) {
        const slot = result.slots.find((s) => s.slotId === id);
        if (slot && isRankCalculationComplete(slot)) {
          resolved.push(id);
        } else if (result.missing_ids.includes(id)) {
          resolved.push(id);
        } else {
          stillPending.push(id);
        }
      }

      if (resolved.length) finish(resolved);
      pendingIds = stillPending;
    } catch {
      // 네트워크 오류 시 다음 틱까지 유지
    }

    if (session.aborted) return;

    if (pendingIds.length === 0) {
      sessions.delete(key);
      return;
    }

    const elapsed = Date.now() - startedAt;
    const delay =
      elapsed < RANK_POLL_MAX_MS
        ? RANK_POLL_INTERVAL_MS
        : RANK_POLL_DELAYED_INTERVAL_MS;
    session.timer = setTimeout(tick, delay);
  };

  session.timer = setTimeout(tick, RANK_POLL_INTERVAL_MS);
}
